/**
 * Taking pictures at the largest resolution the browser exposes, using native still capture where
 * available and an unscaled video frame otherwise.
 *
 * Any camera the browser offers works, including an iPhone through macOS Continuity Camera (it appears as
 * an ordinary video input). The picker lists them once a camera has been allowed; the choice is remembered
 * per browser. The camera needs a secure page (localhost or HTTPS), like QR scanning.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { cameraSupport } from "../qr/useQrScanner";
import {
  listCameras,
  loadCameraId,
  preferredCamera,
  saveCameraId,
  videoConstraints,
  type CameraDevice,
  type CameraFacing,
} from "./devices";

export const JPEG_QUALITY = 0.95;
export const CAMERA_PREVIEW_FPS = 15;

interface FocusCapabilities extends MediaTrackCapabilities {
  focusMode?: string[];
  pointsOfInterest?: { x: number; y: number }[];
}

interface FocusConstraints extends MediaTrackConstraintSet {
  focusMode?: string;
  pointsOfInterest?: { x: number; y: number }[];
}

export type CameraStatus = "idle" | "starting" | "live" | "error";

export type CameraSupport = ReturnType<typeof cameraSupport>;

/** Preserve the video frame's dimensions unless a caller explicitly asks for a smaller image. */
export function frameToBlob(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
  { quality = JPEG_QUALITY, maxEdge = Number.POSITIVE_INFINITY } = {},
): Promise<Blob | null> {
  const width = video.videoWidth;
  const height = video.videoHeight;
  if (!width || !height) return Promise.resolve(null);
  const scale = Math.min(1, maxEdge / Math.max(width, height));
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  const context = canvas.getContext("2d");
  if (!context) return Promise.resolve(null);
  context.drawImage(video, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
}

/** Convert an unsupported image format to JPEG without resizing unless explicitly requested. */
export function imageFileToJpeg(file: File, { quality = JPEG_QUALITY, maxEdge = Number.POSITIVE_INFINITY } = {}) {
  return new Promise<File>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      const scale = Math.min(1, maxEdge / Math.max(image.width, image.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(image.width * scale);
      canvas.height = Math.round(image.height * scale);
      const context = canvas.getContext("2d");
      if (!context) {
        URL.revokeObjectURL(url);
        reject(new Error("The picture could not be read"));
        return;
      }
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(url);
          if (!blob) {
            reject(new Error("The picture could not be converted"));
            return;
          }
          const name = file.name.replace(/\.[^.]+$/, "") || "photo";
          resolve(new File([blob], `${name}.jpg`, { type: "image/jpeg", lastModified: file.lastModified }));
        },
        "image/jpeg",
        quality,
      );
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("That file isn't a picture this browser can read"));
    };
    image.src = url;
  });
}

export interface UseCamera {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  status: CameraStatus;
  error: string | null;
  /** Start the camera (idempotent: calling it while live does nothing). */
  start: () => Promise<void>;
  stop: () => void;
  /** A native still image, or an unscaled JPEG frame; null when the camera is not ready. */
  capture: () => Promise<Blob | null>;
  /** Whether `start()` can work at all in this browser and page. */
  support: CameraSupport;
  /** The cameras on offer; empty until a camera has been allowed (the browser hides them before that). */
  devices: CameraDevice[];
  /** The camera in use, or "" for the browser's default. */
  deviceId: string;
  /** Switch to another camera and remember it. */
  chooseDevice: (deviceId: string) => Promise<void>;
  /** Facing direction, when the browser reports one. */
  facing: CameraFacing | null;
  /** Release the current camera before asking for the front or back one. */
  chooseFacing: (facing: CameraFacing) => Promise<void>;
  /** Only true when the browser exposes a focus point control for this track. */
  canFocus: boolean;
  focusAt: (x: number, y: number) => Promise<boolean>;
}

/** Ask for a photo's maximum size, independent of the live preview's resolution. */
export async function takeNativePhoto(track: MediaStreamTrack): Promise<Blob | null> {
  if (typeof ImageCapture === "undefined") return null;
  try {
    const camera = new ImageCapture(track);
    if (typeof camera.takePhoto !== "function") return null;
    let settings: PhotoSettings | undefined;
    try {
      const caps = await camera.getPhotoCapabilities?.();
      if (caps) {
        settings = {};
        const width = caps.imageWidth?.max;
        const height = caps.imageHeight?.max;
        if (width && width > 0) settings.imageWidth = width;
        if (height && height > 0) settings.imageHeight = height;
      }
    } catch {
      // Some browsers can take a still but cannot report its capabilities.
    }
    let blob: Blob;
    try {
      blob = await camera.takePhoto(settings);
    } catch (error) {
      if (!settings) throw error;
      // A device may reject a width/height combination even though each lies within its range.
      blob = await camera.takePhoto();
    }
    return blob.size ? blob : null;
  } catch {
    return null;
  }
}

async function maximizeVideoResolution(track: MediaStreamTrack, lowPower: boolean) {
  try {
    const capabilities = track.getCapabilities?.();
    if (!capabilities) return;
    const constraints: MediaTrackConstraints = {};
    if (capabilities.width?.max) constraints.width = { ideal: capabilities.width.max };
    if (capabilities.height?.max) constraints.height = { ideal: capabilities.height.max };
    if (lowPower) constraints.frameRate = { ideal: CAMERA_PREVIEW_FPS, max: CAMERA_PREVIEW_FPS };
    if (Object.keys(constraints).length) await track.applyConstraints(constraints);
  } catch {
    // Keep the negotiated stream if the driver cannot apply its advertised maximum.
  }
}

function focusCapabilities(track: MediaStreamTrack): FocusCapabilities | undefined {
  try {
    return track.getCapabilities?.();
  } catch {
    return undefined;
  }
}

/** A camera preview with a shutter; the stream stops when the owning component unmounts. */
export function useCamera({ lowPower = false } = {}): UseCamera {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [status, setStatus] = useState<CameraStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [support] = useState<CameraSupport>(() => {
    const result = cameraSupport();
    return result.supported || !window.isSecureContext
      ? result
      : { supported: false, reason: "This browser can't use a camera here. Attach a photo instead." };
  });
  const [devices, setDevices] = useState<CameraDevice[]>([]);
  const [deviceId, setDeviceId] = useState<string>(() => loadCameraId() ?? "");
  const [facing, setFacing] = useState<CameraFacing | null>(null);
  const [canFocus, setCanFocus] = useState(false);
  const facingRef = useRef<CameraFacing>("environment");
  // The camera to open, read by `start` without making it a new function each time it changes: the
  // stage starts the camera once, and a new `start` would restart it the moment it went live.
  const deviceIdRef = useRef(deviceId);
  // Each open gets a number; a stream that arrives after a newer open, or after `stop`, is closed at once.
  const openSeq = useRef(0);
  const opening = useRef(false);

  const stop = useCallback(() => {
    openSeq.current += 1;
    opening.current = false;
    for (const track of streamRef.current?.getTracks() ?? []) track.stop();
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    if (canvasRef.current) {
      canvasRef.current.width = 0;
      canvasRef.current.height = 0;
      canvasRef.current = null;
    }
    setCanFocus(false);
    setStatus((current) => (current === "error" ? current : "idle"));
  }, []);

  const open = useCallback(
    async (wanted: string, requestedFacing?: CameraFacing) => {
      const seq = (openSeq.current += 1);
      opening.current = true;
      const superseded = () => seq !== openSeq.current;
      let stream: MediaStream | null = null;
      try {
        const constraints = videoConstraints(wanted || null, requestedFacing ?? facingRef.current);
        if (lowPower) constraints.frameRate = { ideal: CAMERA_PREVIEW_FPS, max: CAMERA_PREVIEW_FPS };
        if (requestedFacing) constraints.facingMode = { exact: requestedFacing };
        stream = await navigator.mediaDevices.getUserMedia({ video: constraints, audio: false });
        const video = videoRef.current;
        if (superseded() || !video) {
          for (const track of stream.getTracks()) track.stop();
          return;
        }
        streamRef.current = stream;
        const track = stream.getVideoTracks()[0];
        if (track) {
          await maximizeVideoResolution(track, lowPower);
          if (superseded()) return;
          const capabilities = focusCapabilities(track);
          if (capabilities?.focusMode?.includes("continuous")) {
            try {
              await track.applyConstraints({ advanced: [{ focusMode: "continuous" } as FocusConstraints] });
            } catch {
              // A working camera is still useful when its advertised autofocus setting is rejected.
            }
          }
          if (superseded()) return;
          const supported = navigator.mediaDevices.getSupportedConstraints?.() as
            (MediaTrackSupportedConstraints & { pointsOfInterest?: boolean }) | undefined;
          setCanFocus(
            Boolean(
              supported?.pointsOfInterest &&
              capabilities?.focusMode?.includes("continuous") &&
              "pointsOfInterest" in capabilities,
            ),
          );
        }
        if (superseded()) return;
        video.srcObject = stream;
        await video.play();
        if (superseded()) return;
        const settings = track?.getSettings();
        const actualFacing = settings?.facingMode;
        setFacing(actualFacing === "user" || actualFacing === "environment" ? actualFacing : null);
        if (actualFacing === "user" || actualFacing === "environment") facingRef.current = actualFacing;
        const actual = settings?.deviceId ?? wanted;
        deviceIdRef.current = actual;
        setDeviceId(actual);
        saveCameraId(actual);
        setStatus("live");
        // Names appear after permission; failure to list cameras must not break a working preview.
        const found = await listCameras().catch(() => []);
        if (!superseded()) setDevices(found);
      } catch (cause) {
        for (const track of stream?.getTracks() ?? []) track.stop();
        if (!superseded()) {
          streamRef.current = null;
          if (videoRef.current) videoRef.current.srcObject = null;
          throw cause;
        }
      } finally {
        if (!superseded()) opening.current = false;
      }
    },
    [lowPower],
  );

  const start = useCallback(async () => {
    if (!support.supported || document.visibilityState === "hidden" || streamRef.current || opening.current) return;
    setStatus("starting");
    setError(null);
    try {
      try {
        await open(deviceIdRef.current);
      } catch (cause) {
        if (
          !deviceIdRef.current ||
          !(cause instanceof DOMException) ||
          !["NotFoundError", "OverconstrainedError"].includes(cause.name)
        )
          throw cause;
        // Browser device ids may change across visits or when a camera is unplugged.
        deviceIdRef.current = "";
        await open("");
      }
    } catch (cause) {
      const denied = cause instanceof DOMException && cause.name === "NotAllowedError";
      const missing = cause instanceof DOMException && cause.name === "NotFoundError";
      setError(
        denied
          ? "Camera access was refused. Allow the camera for this site in the browser, or attach a photo instead."
          : missing
            ? "No camera was found. Attach a photo instead."
            : "The camera could not start. Attach a photo instead.",
      );
      setStatus("error");
    }
  }, [open, support.supported]);

  const chooseDevice = useCallback(
    async (next: string) => {
      if (document.visibilityState === "hidden") return;
      setError(null);
      stop();
      deviceIdRef.current = next;
      setDeviceId(next);
      saveCameraId(next);
      setStatus("starting");
      try {
        await open(next);
      } catch {
        setError("That camera could not be opened. It may be in use by another app, or unplugged.");
        setStatus("error");
      }
    },
    [open, stop],
  );

  const chooseFacing = useCallback(
    async (next: CameraFacing) => {
      if (document.visibilityState === "hidden") return;
      setError(null);
      stop();
      deviceIdRef.current = "";
      setDeviceId("");
      facingRef.current = next;
      setStatus("starting");
      try {
        await open("", next);
      } catch {
        setError(
          `The ${next === "environment" ? "back" : "front"} camera could not be opened. Choose an available camera below.`,
        );
        setStatus("error");
      }
    },
    [open, stop],
  );

  const capture = useCallback(async () => {
    const video = videoRef.current;
    if (!video || status !== "live") return null;
    const stream = streamRef.current;
    const seq = openSeq.current;
    const track = stream?.getVideoTracks()[0];
    const photo = track ? await takeNativePhoto(track) : null;
    if (seq !== openSeq.current || stream !== streamRef.current) return null;
    if (photo) return photo;
    const canvas = (canvasRef.current ??= document.createElement("canvas"));
    const frame = await frameToBlob(video, canvas);
    return seq === openSeq.current && stream === streamRef.current ? frame : null;
  }, [status]);

  const focusAt = useCallback(
    async (x: number, y: number) => {
      const stream = streamRef.current;
      const track = stream?.getVideoTracks()[0];
      const seq = openSeq.current;
      if (!track || !canFocus) return false;
      try {
        const constraints: FocusConstraints = {
          pointsOfInterest: [{ x: Math.max(0, Math.min(1, x)), y: Math.max(0, Math.min(1, y)) }],
        };
        const capabilities = focusCapabilities(track);
        if (capabilities?.focusMode?.includes("continuous")) constraints.focusMode = "continuous";
        await track.applyConstraints({ advanced: [constraints] });
        return seq === openSeq.current && stream === streamRef.current;
      } catch {
        return false;
      }
    },
    [canFocus],
  );

  useEffect(() => {
    const suspend = () => {
      if (document.visibilityState === "hidden") stop();
    };
    document.addEventListener("visibilitychange", suspend);
    window.addEventListener("pagehide", stop);
    return () => {
      document.removeEventListener("visibilitychange", suspend);
      window.removeEventListener("pagehide", stop);
    };
  }, [stop]);

  // A camera plugged in or unplugged (an iPhone brought near or taken away) refreshes the list.
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.addEventListener) return undefined;
    let mounted = true;
    const refresh = () =>
      void listCameras()
        .then((found) => {
          if (mounted) setDevices(found);
        })
        .catch(() => undefined);
    navigator.mediaDevices.addEventListener("devicechange", refresh);
    return () => {
      mounted = false;
      navigator.mediaDevices.removeEventListener("devicechange", refresh);
    };
  }, []);

  useEffect(() => stop, [stop]);

  return {
    videoRef,
    status,
    error,
    start,
    stop,
    capture,
    support,
    devices,
    deviceId,
    chooseDevice,
    facing,
    chooseFacing,
    canFocus,
    focusAt,
  };
}

export { preferredCamera };
